import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, AlertCircle, Crown, Sparkles, ArrowRightLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LoyaltyStamp, LoyaltyReward, LoyaltyCustomerSummary } from '../../types';
import { subscribeToUserSummary, subscribeToUserStamps, subscribeToUserRewards, getLoyaltyConfig } from '../../services/loyaltyService';
import { Card } from '../ui/Card';
import StampProgress from './StampProgress';
import RewardsList from './RewardsList';
import { ConvertPointsModal } from './ConvertPointsModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import toast from 'react-hot-toast';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import { db } from '../../lib/firebase';

interface LoyaltyCardProps {
  franchiseId: string;
  branchId?: string;
}

export default function LoyaltyCard({ franchiseId, branchId }: LoyaltyCardProps) {
  const { t } = useTranslation();
  const { user, customClaims } = useAuth();
  const [summary, setSummary] = useState<LoyaltyCustomerSummary | null>(null);
  const [stamps, setStamps] = useState<LoyaltyStamp[]>([]);
  const [rewards, setRewards] = useState<LoyaltyReward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loyaltyConfig, setLoyaltyConfig] = useState<any>(null);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const [isVIP, setIsVIP] = useState(false);
  const [requestingVIP, setRequestingVIP] = useState(false);
  const [stampCardsCount, setStampCardsCount] = useState(0); // Real-time stamp_cards count

  // ALL hooks must be called before any early return (React rules)
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = subscribeToUserSummary(user.id, (data) => {
      setSummary(data);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Real-time subscription for stamps (loyalty_stamps collection)
  useEffect(() => {
    if (!user?.id || !franchiseId) return;

    const unsubStamps = subscribeToUserStamps(user.id, franchiseId, (stampsData) => {
      setStamps(stampsData);
    });

    return () => unsubStamps();
  }, [user?.id, franchiseId]);

  // Real-time subscription for rewards (loyalty_rewards collection)
  useEffect(() => {
    if (!user?.id || !franchiseId) return;

    const unsubRewards = subscribeToUserRewards(user.id, franchiseId, (rewardsData) => {
      setRewards(rewardsData);
      setLoading(false);
    });

    return () => unsubRewards();
  }, [user?.id, franchiseId]);

  // One-time load for loyalty config (rarely changes)
  useEffect(() => {
    if (!franchiseId) return;

    let mounted = true;
    getLoyaltyConfig(franchiseId)
      .then((config) => {
        if (mounted) setLoyaltyConfig(config);
      })
      .catch((err) => {
        if (mounted) setError(err instanceof Error ? err.message : t('errors.loadingData'));
      });

    return () => { mounted = false; };
  }, [franchiseId]);

  // Subscribe to user points and VIP status in real-time
  useEffect(() => {
    if (!user?.id) return;

    const userDocRef = doc(db, 'users', user.id);
    const unsubscribe = onSnapshot(userDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const userData = snapshot.data();
        setUserPoints(userData.queuePoints || 0);
        setIsVIP(userData.isVIP || false);
      }
    });

    return () => unsubscribe();
  }, [user?.id]);

  // Subscribe to stamp_cards collection in real-time (for conversions from points)
  useEffect(() => {
    if (!user?.id || !franchiseId) return;

    const stampCardsQuery = query(
      collection(db, 'stamp_cards'),
      where('userId', '==', user.id),
      where('franchiseId', '==', franchiseId),
      where('status', '==', 'active')
    );

    const unsubscribe = onSnapshot(stampCardsQuery, (snapshot) => {
      const totalStamps = snapshot.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.stamps || 0);
      }, 0);
      setStampCardsCount(totalStamps);
    }, (error) => {
      console.error('[LoyaltyCard] Error in stamp_cards listener:', error);
    });

    return () => unsubscribe();
  }, [user?.id, franchiseId]);

  // Early return AFTER all hooks (React rules: hooks must always be called in same order)
  if (!user || user.role === 'guest') {
    return null;
  }

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-surface-2 rounded w-1/3"></div>
          <div className="h-24 bg-surface-2 rounded"></div>
          <div className="h-12 bg-surface-2 rounded"></div>
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-3 text-danger">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      </Card>
    );
  }

  // Combine stamps from both systems: loyalty_stamps (old) + stamp_cards (new)
  const legacyStamps = stamps.filter(s => s.status === 'active').length;
  const activeStamps = legacyStamps + stampCardsCount;
  const franchiseData = summary?.franchises?.[franchiseId];
  const requiredStamps = franchiseData?.currentProgress?.required || 10;
  const progress = (activeStamps / requiredStamps) * 100;

  // Handle VIP request
  const handleRequestVIP = async () => {
    if (userPoints < 500) {
      toast.error(t('loyalty.vip.needPoints'));
      return;
    }

    setRequestingVIP(true);
    try {
      const getVIPStatusFn = httpsCallable(functions, 'getVIPStatus');
      await getVIPStatusFn({ franchiseId });

      toast.success(t('loyalty.vip.requestSent'));
    } catch (error: any) {
      console.error('Error requesting VIP:', error);
      toast.error(error.message || t('loyalty.vip.error'));
    } finally {
      setRequestingVIP(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Main Card */}
      <Card className="p-6 border-2 border-info/30">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-ink">
                {t('loyalty.card.title')}
              </h2>
              <p className="text-sm text-ink-muted mt-1">
                {t('loyalty.card.subtitle')}
              </p>
            </div>
            <div className="bg-surface rounded-lg px-4 py-2 shadow-sm">
              <div className="text-xs text-ink-subtle">{t('loyalty.stats.rewards')}</div>
              <div className="text-2xl font-bold text-info">
                {rewards.filter(r => r.status === 'active' || r.status === 'generated').length}
              </div>
            </div>
          </div>

          {/* Stamps Progress */}
          <StampProgress
            activeStamps={activeStamps}
            requiredStamps={requiredStamps}
            progress={progress}
          />

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-surface rounded-lg p-3 text-center">
              <div className="text-xs text-ink-subtle">{t('loyalty.stats.stamps')}</div>
              <div className="text-xl font-bold text-ink">
                {activeStamps}
              </div>
            </div>
            <div className="bg-surface rounded-lg p-3 text-center">
              <div className="text-xs text-ink-subtle">{t('loyalty.stats.redeemed')}</div>
              <div className="text-xl font-bold text-ink">
                {franchiseData?.totalRewardsRedeemed || 0}
              </div>
            </div>
            <div className="bg-surface rounded-lg p-3 text-center">
              <div className="text-xs text-ink-subtle">{t('loyalty.stats.total')}</div>
              <div className="text-xl font-bold text-ink">
                {franchiseData?.totalStampsEarned || 0}
              </div>
            </div>
          </div>

          {/* Points & VIP Section - Only show if points system is enabled */}
          {loyaltyConfig?.pointsSystemEnabled && (
          <div className="bg-surface rounded-lg p-4 space-y-4">
            {/* Points Display */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium text-ink-muted">{t('loyalty.points.yourPoints')}</span>
              </div>
              <span className="text-2xl font-bold text-accent">{userPoints}</span>
            </div>

            {/* VIP Status Section */}
            <div className="border-t border-hairline pt-4">
              {(isVIP || customClaims?.isVIP) ? (
                /* VIP Active Badge */
                <div className="border-2 border-success/30 rounded-lg p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <img
                        src="/logo/logo_gorilla_sonriendo-sin-fondo.webp"
                        alt="VIP"
                        className="w-32 h-32 object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-bold text-success">{t('loyalty.vip.youAreVip')}</h4>
                        <span className="px-2 py-0.5 text-white text-xs font-bold rounded-full shadow-sm animate-pulse">
                          {t('loyalty.vip.active')}
                        </span>
                      </div>
                      <p className="text-sm text-success font-medium">
                        {t('loyalty.vip.benefits')}
                      </p>
                      <div className="text-xs text-success mt-1 space-y-0.5">
                        <p>{t('loyalty.vip.benefit1')}</p>
                        <p>{t('loyalty.vip.benefit2')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* Non-VIP: Show Request Button */
                <>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-surface-2 flex items-center justify-center grayscale opacity-60">
                      <Crown className="w-6 h-6 text-accent" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-ink mb-1">{t('loyalty.vip.status')}</h4>
                      <p className="text-xs text-ink-muted">
                        {t('loyalty.vip.requirement', { points: 500 })}
                      </p>
                      <div className="mt-2">
                        <div className="h-2 bg-surface-2 rounded-full overflow-hidden">
                          <div
                            className="h-full transition-all duration-500"
                            style={{ width: `${Math.min((userPoints / 500) * 100, 100)}%` }}
                          />
                        </div>
                        <p className="text-xs text-ink-subtle mt-1">
                          {userPoints >= 500 ? t('loyalty.vip.canRequest') : t('loyalty.vip.pointsNeeded', { points: 500 - userPoints })}
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleRequestVIP}
                    disabled={userPoints < 500 || requestingVIP}
                    className={`mt-3 w-full py-2 px-4 rounded-lg font-medium text-sm transition-all ${
                      userPoints >= 500
                        ? 'text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                        : 'bg-surface-2 text-ink-subtle cursor-not-allowed grayscale opacity-60'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Crown className="w-4 h-4" />
                      <span>{requestingVIP ? t('loyalty.vip.sendingRequest') : t('loyalty.card.requestVip')}</span>
                    </div>
                  </button>
                </>
              )}
            </div>

            {/* Convert Points to Stamps */}
            {loyaltyConfig?.pointsToStampsEnabled && (
              <div className="border-t border-hairline pt-4">
                <div className="rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <ArrowRightLeft className="w-4 h-4 text-accent" />
                    <span className="text-sm font-semibold text-accent">
                      {t('loyalty.conversion.toStamps')}
                    </span>
                  </div>
                  <p className="text-xs text-accent">
                    {t('loyalty.conversion.pointsEqual', { points: loyaltyConfig?.pointsPerStamp || 100 })}
                  </p>
                </div>
                <button
                  onClick={() => setShowConvertModal(true)}
                  disabled={userPoints < (loyaltyConfig?.pointsPerStamp || 100)}
                  className="w-full py-2.5 px-4 rounded-lg font-medium text-sm transition-all
                    text-white
                   
                    disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('loyalty.conversion.redeemPoints')}
                </button>
                {userPoints < (loyaltyConfig?.pointsPerStamp || 100) && (
                  <p className="text-xs text-ink-subtle mt-2 text-center">
                    {t('loyalty.conversion.minPoints', { points: loyaltyConfig?.pointsPerStamp || 100 })}
                  </p>
                )}
              </div>
            )}
          </div>
          )}

          {/* Expiration Warning */}
          {stamps.some(s => {
            if (!s.expiresAt) return false;
            const expiryDate = s.expiresAt.toDate();
            const daysUntilExpiry = Math.ceil((expiryDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            return s.status === 'active' && daysUntilExpiry <= 7;
          }) && (
            <div className="bg-accent/10 border border-accent/30 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Calendar className="w-5 h-5 text-accent mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-accent">
                    {t('loyalty.card.expiringStamps')}
                  </p>
                  <p className="text-xs text-accent mt-1">
                    {t('loyalty.card.expiringWarning')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Rewards List */}
      <RewardsList
        rewards={rewards}
        franchiseId={franchiseId}
      />

      {/* Convert Points Modal */}
      <ConvertPointsModal
        isOpen={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        currentPoints={userPoints}
        pointsPerStamp={loyaltyConfig?.pointsPerStamp || 100}
        franchiseId={franchiseId}
        branchId={branchId}
      />
    </div>
  );
}
